import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Package, Clock, Truck, CheckCircle2, XCircle, Phone, Search, ArrowLeft } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STEPS = [
  { key: 'pending', label: 'In Attesa', icon: Clock, color: '#D98324' },
  { key: 'confirmed', label: 'Confermato', icon: CheckCircle2, color: '#4A6B53' },
  { key: 'shipped', label: 'Spedito', icon: Truck, color: '#0088cc' },
  { key: 'delivered', label: 'Consegnato', icon: CheckCircle2, color: '#2D5A3D' },
];

export default function OrderTrackingPage() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [emailOrders, setEmailOrders] = useState(null);
  const [searchingEmail, setSearchingEmail] = useState(false);

  useEffect(() => {
    if (orderId) fetchOrder(orderId);
  }, [orderId]);

  const fetchOrder = async (id) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tracking/${id}`);
      setOrder(data);
    } catch {
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const searchByEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSearchingEmail(true);
    try {
      const { data } = await axios.get(`${API}/tracking/lookup/by-email`, { params: { email: email.trim() } });
      setEmailOrders(data.orders || []);
    } catch {
      setEmailOrders([]);
    } finally {
      setSearchingEmail(false);
    }
  };

  const currentStepIdx = order ? STATUS_STEPS.findIndex(s => s.key === order.status) : -1;
  const isCancelled = order?.status === 'cancelled';

  return (
    <div data-testid="order-tracking-page" className="pb-safe max-w-lg mx-auto">
      <div className="bg-white border-b border-[#E8E3D9] px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Link
            data-testid="tracking-back-btn"
            to={searchParams.get('from') ? `/negozio/${searchParams.get('from')}` : '/'}
            className="p-1.5 rounded-full hover:bg-[#F0EBE1]"
          >
            <ArrowLeft size={20} className="text-[#2A2624]" />
          </Link>
          <h1 className="text-lg font-semibold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
            Traccia il tuo Ordine
          </h1>
        </div>
      </div>

      {!orderId && !order && (
        <div className="px-4 pt-6 space-y-6">
          {/* Search by email */}
          <div className="bg-white rounded-xl border border-[#E8E3D9] p-4 space-y-3">
            <h3 className="font-medium text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>Cerca i tuoi ordini</h3>
            <form onSubmit={searchByEmail} className="flex gap-2">
              <Input
                data-testid="tracking-email"
                type="email"
                placeholder="Inserisci la tua email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="h-12 rounded-xl border-[#E8E3D9] flex-1"
              />
              <Button
                data-testid="tracking-search-btn"
                type="submit"
                disabled={searchingEmail}
                className="h-12 px-4 rounded-xl bg-[#C25934] hover:bg-[#A64B2B] text-white"
              >
                <Search size={18} />
              </Button>
            </form>
          </div>

          {emailOrders !== null && (
            <div className="space-y-3">
              {emailOrders.length === 0 ? (
                <div className="text-center py-8">
                  <Package size={32} className="mx-auto text-[#877E78] mb-2" />
                  <p className="text-[#877E78]">Nessun ordine trovato per questa email</p>
                </div>
              ) : (
                emailOrders.map(o => (
                  <Link
                    key={o.id}
                    to={`/tracking/${o.id}`}
                    data-testid={`order-link-${o.id}`}
                    className="block bg-white rounded-xl border border-[#E8E3D9] p-4 hover:border-[#C25934]/50 transition-colors"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-xs text-[#877E78]">#{o.id?.slice(0, 8)}</p>
                        <p className="font-medium text-sm text-[#2A2624]">{o.customer_name}</p>
                        <p className="text-xs text-[#877E78]">{new Date(o.created_at).toLocaleDateString('it-IT')}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-[#C25934]">{o.total?.toFixed(2)} &euro;</p>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F0EBE1] text-[#5C554F]">
                          {o.status}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="px-4 pt-6 space-y-4">
          <div className="h-20 skeleton-shimmer rounded-xl" />
          <div className="h-32 skeleton-shimmer rounded-xl" />
        </div>
      )}

      {order && (
        <div className="px-4 pt-6 space-y-5">
          {/* Order Header */}
          <div className="bg-white rounded-xl border border-[#E8E3D9] p-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs text-[#877E78]">Ordine #{order.order_id?.slice(0, 8)}</p>
                <h2 className="text-xl font-bold text-[#2A2624] mt-1" style={{ fontFamily: 'Outfit' }}>
                  {order.total?.toFixed(2)} &euro;
                </h2>
                <p className="text-sm text-[#5C554F]">{order.customer_name}</p>
              </div>
              {order.shop_name && (
                <div className="text-right">
                  <p className="text-xs font-medium text-[#C25934]">{order.shop_name}</p>
                  {order.shop_phone && (
                    <a href={`tel:${order.shop_phone}`} className="flex items-center gap-1 text-xs text-[#877E78] mt-1">
                      <Phone size={10} /> {order.shop_phone}
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Status Timeline */}
          <div className="bg-white rounded-xl border border-[#E8E3D9] p-4">
            <h3 className="text-sm font-bold text-[#2A2624] uppercase tracking-wider mb-4">Stato Ordine</h3>
            {isCancelled ? (
              <div className="flex items-center gap-3 p-3 bg-red-50 rounded-xl">
                <XCircle size={24} className="text-red-500" />
                <div>
                  <p className="font-medium text-red-600">Ordine Annullato</p>
                  <p className="text-xs text-red-400">Contatta il negozio per informazioni</p>
                </div>
              </div>
            ) : (
              <div className="space-y-0">
                {STATUS_STEPS.map((step, idx) => {
                  const StepIcon = step.icon;
                  const isCompleted = idx <= currentStepIdx;
                  const isCurrent = idx === currentStepIdx;
                  return (
                    <div key={step.key} className="flex items-start gap-3">
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          isCompleted ? '' : 'bg-[#F0EBE1]'
                        }`} style={isCompleted ? { backgroundColor: step.color } : {}}>
                          <StepIcon size={16} className={isCompleted ? 'text-white' : 'text-[#877E78]'} />
                        </div>
                        {idx < STATUS_STEPS.length - 1 && (
                          <div className={`w-0.5 h-8 ${isCompleted && idx < currentStepIdx ? 'bg-[#4A6B53]' : 'bg-[#E8E3D9]'}`} />
                        )}
                      </div>
                      <div className="pt-1">
                        <p className={`text-sm font-medium ${isCurrent ? 'text-[#2A2624]' : isCompleted ? 'text-[#5C554F]' : 'text-[#877E78]'}`}>
                          {step.label}
                        </p>
                        {isCurrent && (
                          <p className="text-[10px] text-[#C25934] font-bold mt-0.5">Stato attuale</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Items */}
          <div className="bg-white rounded-xl border border-[#E8E3D9] p-4">
            <h3 className="text-sm font-bold text-[#2A2624] uppercase tracking-wider mb-3">Articoli</h3>
            {order.items?.map((item, idx) => (
              <div key={idx} className="flex justify-between items-center py-2 border-b border-[#E8E3D9] last:border-0">
                <div>
                  <p className="text-sm text-[#2A2624]">{item.title}</p>
                  {Object.keys(item.variants || {}).length > 0 && (
                    <p className="text-[10px] text-[#877E78]">
                      {Object.entries(item.variants).map(([k, v]) => `${k}: ${v}`).join(' | ')}
                    </p>
                  )}
                </div>
                <p className="text-sm font-medium text-[#5C554F]">{item.quantity}x {item.price?.toFixed(2)} &euro;</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
